import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentMethodEnum } from '@/__generated_graphql__/graphql';
import { WAY_TO_METHODS } from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.constants';
import {
  PaymentOption,
  ViewMethod
} from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.types';
import { CustomRadio } from '@/components/AdvertisePayments/components/Options/OptionsCustomRadio';
import { usePaymentOptions } from '@/components/AdvertisePayments/hooks/usePaymentOptions';
import { PaymentWaySkeleton } from '@/components/skeletons/AdvertisePaymentsSkeleton/PaymentWaySkeleton';
import { PaymentWayKey } from '@/constants/common';
import { PAYMENT_MODAL_CY } from '@/constants/test-id';
import { useBelow } from '@/hooks/useBelow';

import * as S from './ExtraContent.styles';

type ModalExtraContentPaymentWayProps = {
  selectedPaymentWay: PaymentMethodEnum;
  methods: ViewMethod[];
  onChange: (method: PaymentMethodEnum) => void;
  paidServiceLoading?: boolean;
};

export const ModalExtraContentPaymentWay: FC<
  ModalExtraContentPaymentWayProps
> = ({ selectedPaymentWay, onChange, methods, paidServiceLoading = false }) => {
  const { t } = useTranslation();
  const isBelowMobileL = useBelow('mobileL');

  const availableMethod = useMemo(
    () => new Set(methods.map(method => method.id as PaymentMethodEnum)),
    [methods]
  );

  const pickByWay = (way: PaymentWayKey): PaymentMethodEnum | null =>
    WAY_TO_METHODS[way].find(method => availableMethod.has(method)) ?? null;

  const handleSelect = (key: PaymentWayKey) => {
    const method = pickByWay(key);
    if (method) onChange(method);
  };

  const paymentOptions: PaymentOption[] = usePaymentOptions({
    methods,
    isMobile: isBelowMobileL,
    t
  });

  return (
    <S.PaymentMethodSection>
      <S.PaymentMethodCard>
        <S.PaymentMethodHeader>
          <S.PaymentMethodTitle>
            {t('Advertise.payment_method_label')}
          </S.PaymentMethodTitle>
        </S.PaymentMethodHeader>

        {paidServiceLoading ? (
          <PaymentWaySkeleton />
        ) : (
          <S.PaymentCheckboxList>
            {paymentOptions.map(({ key, label, extra }) => {
              const method = pickByWay(key);
              if (!method) return null;
              return (
                <CustomRadio
                  key={key}
                  name="paymentWay"
                  value={method}
                  label={label}
                  data-cy={PAYMENT_MODAL_CY.PAYMENT_METHOD(key)}
                  isChecked={selectedPaymentWay === method}
                  onChange={() => handleSelect(key)}
                >
                  {extra}
                </CustomRadio>
              );
            })}
          </S.PaymentCheckboxList>
        )}
      </S.PaymentMethodCard>
    </S.PaymentMethodSection>
  );
};
